/**
 * SmartImprove Dashboard (Consultant) — sibling of SmartEvaluate Dashboard.
 *
 * Visually + functionally aligned with AssessmentsListPage:
 *   - Same page title / subtitle treatment
 *   - Same 5-card summary strip (Total / In Progress / Ready for Review /
 *     Finalized / Published)
 *   - Same search + status + type + sort filter bar
 *   - Same table layout (Company / Contact / Type / Progress / Status /
 *     Last Updated / Actions)
 *   - Same status badge colors (Orange / Blue / Green / Purple)
 *   - Same View + Open Snapshot + Delete action buttons
 *   - Clickable company name → engagement detail
 */
import { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useConsultant } from "./ConsultantLayout";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import {
  ChevronRight,
  Search,
  ClipboardList,
  Clock,
  ClipboardCheck,
  Lock,
  Send,
  Plus,
  Eye,
  Copy,
  LayoutDashboard,
  Trash2,
  Archive as ArchiveIcon,
  ArchiveRestore,
} from "lucide-react";
import {
  listEngagements,
  getEngagement,
  createEngagement,
  duplicateEngagement,
  archiveEngagement,
  restoreEngagement,
} from "../lib/smartimproveApi";
import ArchiveDialog from "../components/ArchiveDialog";
import RowActionsMenu from "../components/RowActionsMenu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/smartimprove`;
const ax = axios.create({ baseURL: API, withCredentials: true });

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function formatLocalDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}

// ---------------------------------------------------------------------------
// Lifecycle status — mirrors SmartEvaluate exactly (Orange/Blue/Green/Purple).
// Precedence:
//   Published        — engagement.is_published OR status="published"
//   Finalized        — engagement.is_finalized
//   Ready for Review — status="ready_for_review"
//   In Progress      — everything else
// ---------------------------------------------------------------------------
function deriveStatus(e) {
  // Soft-archive wins over every other lifecycle state — archived rows are
  // operationally inert until restored.
  if ((e.status || "").toLowerCase() === "archived") return "Archived";
  if (e.is_published || (e.status || "").toLowerCase() === "published") {
    return "Published";
  }
  if (e.is_finalized || (e.status || "").toLowerCase() === "finalized") {
    return "Finalized";
  }
  if ((e.status || "").toLowerCase() === "ready_for_review") {
    return "Ready for Review";
  }
  return "In Progress";
}

// Rough completion percentage from status + finalization signals.
function completionPct(e) {
  if (e.is_published) return 100;
  if (e.is_finalized) return 100;
  if ((e.status || "").toLowerCase() === "ready_for_review") return 100;
  if (typeof e.progress_percent === "number") return e.progress_percent;
  // Heuristic: tally the consultant inputs that exist.
  let stages = 0;
  if (e.has_sites || (e.site_count || 0) > 0) stages += 1;
  if (e.has_findings) stages += 1;
  if (e.has_recommendations) stages += 1;
  if (e.has_implementation) stages += 1;
  if (e.has_roi) stages += 1;
  return Math.max(0, Math.min(100, Math.round((stages / 5) * 100)));
}

const STATUS_META = {
  "In Progress": {
    pill: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
    accent: "text-[#C2410C]",
    icon: Clock,
    tooltip: "Consultant is still building out the engagement",
  },
  "Ready for Review": {
    pill: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
    accent: "text-[#1D4ED8]",
    icon: ClipboardCheck,
    tooltip: "Engagement ready for stakeholder review",
  },
  Finalized: {
    pill: "bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]",
    accent: "text-[#047857]",
    icon: Lock,
    tooltip: "Engagement finalized — immutable snapshot",
  },
  Published: {
    pill: "bg-[#F0EDF9] text-[#5847B0] border-[#DDD6FE]",
    accent: "text-[#5847B0]",
    icon: Send,
    tooltip: "Business case published to the client portal",
  },
  Archived: {
    pill: "bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]",
    accent: "text-[#6B7280]",
    icon: ArchiveIcon,
    tooltip: "Soft-archived. Hidden from the default list view — restore to reactivate.",
  },
};

function StatusBadge({ status, testId }) {
  const meta = STATUS_META[status];
  if (!meta) {
    return (
      <span
        data-testid={testId}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
      >
        {status}
      </span>
    );
  }
  const Icon = meta.icon;
  return (
    <span
      data-testid={testId}
      title={meta.tooltip}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${meta.pill}`}
    >
      {(status === "Published" || status === "Finalized" || status === "Archived") && (
        <Icon className="w-3 h-3" strokeWidth={2} />
      )}
      {status}
    </span>
  );
}

function SummaryCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex-1 bg-white border border-[#E5E7EB] rounded-md px-4 py-3 shadow-sm"
    >
      <div className="flex items-center gap-2 text-[11px] text-[#6B7280] font-medium">
        <Icon className={`w-3.5 h-3.5 ${accent}`} strokeWidth={1.75} />
        {label}
      </div>
      <div
        className="mt-1.5 text-2xl font-bold text-[#111827]"
        style={{ fontFamily: "Chivo, sans-serif" }}
      >
        {value}
      </div>
    </div>
  );
}

const TYPE_LABEL = {
  remote: "Remote",
  remote_review: "Remote Review",
  workshop: "Workshop",
  hybrid: "Hybrid",
  post_go_live: "Post Go Live",
  optimization: "Optimization",
};

// Convert a raw engagement_type slug (e.g. "post_go_live") into a display
// label. Falls back to title-cased spaced words when the key isn't in the
// map above. UI-only — never mutates stored DB values.
const formatEngagementType = (raw) => {
  if (!raw) return "—";
  if (TYPE_LABEL[raw]) return TYPE_LABEL[raw];
  return String(raw)
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

export default function SmartImproveListPage() {
  const me = useConsultant();
  const isAdmin = (me?.role || "").toLowerCase() === "admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  // Filter state — same shape as SmartEvaluate.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sortOrder, setSortOrder] = useState("desc");

  // Admin delete state.
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Archive flow state. Pending = engagement currently in the modal; restoring
  // tracks the id whose Restore button is busy.
  const [pendingArchive, setPendingArchive] = useState(null);
  const [restoringId, setRestoringId] = useState(null);

  const reload = useCallback(() => {
    setLoading(true);
    return listEngagements()
      .then((data) => setItems(data || []))
      .catch(() => setErr("Failed to load SmartImprove engagements"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleConfirmDelete = async () => {
    if (!confirmDelete?.id) return;
    setDeleting(true);
    try {
      await ax.delete(`/engagements/${confirmDelete.id}`);
      setItems((prev) => prev.filter((a) => a.id !== confirmDelete.id));
      toast.success("Engagement deleted");
      setConfirmDelete(null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      if (status === 409) {
        toast.error(
          "This engagement is finalized and cannot be deleted (immutable snapshot guarantee)."
        );
      } else {
        toast.error(`Delete failed · ${detail}`);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmArchive = async ({ reason, notes }) => {
    if (!pendingArchive?.id) return;
    const updated = await archiveEngagement(pendingArchive.id, {
      archive_reason: reason,
      archive_notes: notes,
    });
    setItems((prev) =>
      prev.map((a) => (a.id === pendingArchive.id ? { ...a, ...updated } : a)),
    );
    toast.success(`${pendingArchive.company_name || "Engagement"} archived`);
  };

  const handleRestore = async (a) => {
    setRestoringId(a.id);
    try {
      const updated = await restoreEngagement(a.id);
      setItems((prev) =>
        prev.map((x) => (x.id === a.id ? { ...x, ...updated } : x)),
      );
      toast.success(`${a.company_name || "Engagement"} restored to In Progress`);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      toast.error(`Restore failed · ${detail}`);
    } finally {
      setRestoringId(null);
    }
  };

  const handleDuplicate = async (row) => {
    try {
      const dupe = await duplicateEngagement(row.id);
      toast.success(`Duplicated to "${dupe.company_name || "copy"}"`);
      navigate(`/consultant/smartimprove/${dupe.id}`);
    } catch {
      toast.error("Failed to duplicate engagement");
    }
  };

  const enriched = useMemo(
    () =>
      items.map((a) => ({
        ...a,
        _status: deriveStatus(a),
        _pct: completionPct(a),
      })),
    [items]
  );

  const summary = useMemo(() => {
    const s = {
      total: enriched.length,
      inProgress: 0,
      readyForReview: 0,
      finalized: 0,
      published: 0,
    };
    for (const a of enriched) {
      if (a._status === "In Progress") s.inProgress += 1;
      else if (a._status === "Ready for Review") s.readyForReview += 1;
      else if (a._status === "Finalized") s.finalized += 1;
      else if (a._status === "Published") s.published += 1;
    }
    return s;
  }, [enriched]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = enriched.filter((a) => {
      if (q) {
        const hay = `${a.company_name || ""} ${a.contact_name || ""} ${
          a.email || ""
        }`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      // Hide Archived rows unless the consultant explicitly filters for
      // them — "All statuses" intentionally means "everything except
      // archived" per the soft-archive product spec.
      if (statusFilter === "all") {
        if (a._status === "Archived") return false;
      } else if (a._status !== statusFilter) {
        return false;
      }
      if (typeFilter !== "all" && a.engagement_type !== typeFilter) return false;
      return true;
    });
    rows = rows.slice().sort((x, y) => {
      const xt = x.updated_at ? new Date(x.updated_at).getTime() : 0;
      const yt = y.updated_at ? new Date(y.updated_at).getTime() : 0;
      return sortOrder === "desc" ? yt - xt : xt - yt;
    });
    return rows;
  }, [enriched, search, statusFilter, typeFilter, sortOrder]);

  return (
    <div data-testid="smartimprove-dashboard">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h1
          className="text-xl font-bold text-[#111827]"
          style={{ fontFamily: "Chivo" }}
        >
          SmartImprove Dashboard — Consultant Workspace
        </h1>
        <Link
          to="/improve/start"
          data-testid="smartimprove-new-engagement-btn"
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs font-semibold shadow-sm transition-colors whitespace-nowrap"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
          New Engagement
        </Link>
      </div>
      <p className="text-xs text-[#6B7280] mb-5">
        Review operational improvement engagements, track progress, validate
        recommendations, and prepare boardroom-ready business cases.
      </p>

      {err && (
        <div className="mb-3 px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-600">
          {err}
        </div>
      )}

      {/* Summary cards — mirror SmartEvaluate 5-card strip */}
      <div
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-5"
        data-testid="smartimprove-summary-cards"
      >
        <SummaryCard
          icon={ClipboardList}
          label="Total Engagements"
          value={summary.total}
          accent="text-[#025979]"
          testId="smartimprove-summary-total"
        />
        <SummaryCard
          icon={Clock}
          label="In Progress"
          value={summary.inProgress}
          accent={STATUS_META["In Progress"].accent}
          testId="smartimprove-summary-in-progress"
        />
        <SummaryCard
          icon={ClipboardCheck}
          label="Ready for Review"
          value={summary.readyForReview}
          accent={STATUS_META["Ready for Review"].accent}
          testId="smartimprove-summary-ready-for-review"
        />
        <SummaryCard
          icon={Lock}
          label="Finalized"
          value={summary.finalized}
          accent={STATUS_META.Finalized.accent}
          testId="smartimprove-summary-finalized"
        />
        <SummaryCard
          icon={Send}
          label="Published"
          value={summary.published}
          accent={STATUS_META.Published.accent}
          testId="smartimprove-summary-published"
        />
      </div>

      {/* Filter bar */}
      <div
        className="bg-white border border-[#E5E7EB] rounded-md px-3 py-2.5 mb-3 flex flex-wrap items-center gap-2"
        data-testid="smartimprove-filter-bar"
      >
        <div className="relative flex-1 min-w-[220px]">
          <Search
            className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-2.5 top-1/2 -translate-y-1/2"
            strokeWidth={1.75}
          />
          <input
            data-testid="smartimprove-filter-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search company or contact…"
            className="w-full pl-8 pr-3 py-1.5 text-xs border border-[#E5E7EB] rounded-sm focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979] text-[#111827] placeholder:text-[#9CA3AF]"
          />
        </div>
        <select
          data-testid="smartimprove-filter-status-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979]"
        >
          <option value="all">All statuses</option>
          <option value="In Progress">In Progress</option>
          <option value="Ready for Review">Ready for Review</option>
          <option value="Finalized">Finalized</option>
          <option value="Published">Published</option>
          <option value="Archived">Archived</option>
        </select>
        <select
          data-testid="smartimprove-filter-type-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979]"
        >
          <option value="all">All types</option>
          <option value="remote">Remote</option>
          <option value="workshop">Workshop</option>
          <option value="hybrid">Hybrid</option>
        </select>
        <select
          data-testid="smartimprove-filter-sort-select"
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
          className="px-2.5 py-1.5 text-xs border border-[#E5E7EB] rounded-sm bg-white text-[#111827] focus:outline-none focus:ring-1 focus:ring-[#025979] focus:border-[#025979]"
        >
          <option value="desc">Last Updated ↓</option>
          <option value="asc">Last Updated ↑</option>
        </select>
      </div>

      {/* Table */}
      <div
        className="bg-white border border-[#E5E7EB] rounded-md shadow-sm overflow-hidden"
        data-testid="smartimprove-table"
      >
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px]">
                Company
              </th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px]">
                Contact
              </th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-28">
                Type
              </th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-40">
                Status
              </th>
              <th className="text-left px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-44">
                Last Updated
              </th>
              <th className="text-right px-4 py-2.5 text-[#6B7280] font-semibold uppercase tracking-wide text-[10px] w-14"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-[#9CA3AF]">
                  Loading…
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-10 text-center text-[#9CA3AF]">
                  {enriched.length === 0
                    ? "No engagements yet. Click New Engagement to get started."
                    : "No engagements match the current filters."}
                </td>
              </tr>
            ) : (
              filtered.map((a) => {
                const detailHref = `/consultant/smartimprove/${a.id}`;
                const isPublishedOrFinal =
                  a._status === "Published" || a._status === "Finalized";
                const isArchived = a._status === "Archived";
                return (
                  <tr
                    key={a.id}
                    data-testid={`smartimprove-row-${a.id}`}
                    className={`border-b border-[#E5E7EB] hover:bg-[#F8FAFC] transition-colors ${
                      isArchived ? "bg-[#FAFAFA] text-[#9CA3AF]" : ""
                    }`}
                  >
                    <td className="px-4 py-3.5 text-[#111827] font-semibold">
                      <Link
                        to={detailHref}
                        className="hover:text-[#025979]"
                        data-testid={`smartimprove-link-${a.id}`}
                      >
                        {a.company_name || "—"}
                      </Link>
                    </td>
                    <td className="px-4 py-3.5 text-[#374151]">
                      <div>{a.contact_name || "—"}</div>
                      <div className="text-[10px] text-[#9CA3AF] mt-0.5">
                        {a.email}
                      </div>
                    </td>
                    <td className="px-4 py-3.5 text-[#374151]">
                      {formatEngagementType(a.engagement_type)}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge
                        status={a._status}
                        testId={`smartimprove-status-${a.id}`}
                      />
                    </td>
                    <td
                      className="px-4 py-3.5 text-[11px] text-[#374151]"
                      data-testid={`smartimprove-updated-${a.id}`}
                    >
                      {formatLocalDateTime(a.updated_at)}
                    </td>
                    <td className="px-4 py-3.5 text-right">
                      <RowActionsMenu
                        testId={`smartimprove-row-actions-${a.id}`}
                        ariaLabel={`Actions for ${a.company_name || "engagement"}`}
                        actions={[
                          {
                            id: "view",
                            label: "View Details",
                            icon: Eye,
                            testId: `smartimprove-action-view-${a.id}`,
                            onClick: () => navigate(detailHref),
                          },
                          {
                            id: "duplicate",
                            label: "Duplicate",
                            icon: Copy,
                            // Deep-copies findings + warehouses into a new
                            // "copy - <name>" engagement. Hidden for archived.
                            visible: !isArchived,
                            testId: `smartimprove-action-duplicate-${a.id}`,
                            onClick: () => handleDuplicate(a),
                          },
                          {
                            id: "dashboard",
                            label: "Open Client Dashboard",
                            icon: LayoutDashboard,
                            // Same trigger as legacy "Open Snapshot" — only
                            // surface this when the engagement has been
                            // published OR finalized with a snapshot id.
                            visible:
                              isPublishedOrFinal && !!a.finalized_version_id,
                            testId: `smartimprove-action-open-dashboard-${a.id}`,
                            onClick: () =>
                              navigate(
                                `${detailHref}?snapshot=${a.finalized_version_id}`,
                              ),
                          },
                          {
                            id: "restore",
                            label: restoringId === a.id ? "Restoring…" : "Restore",
                            icon: ArchiveRestore,
                            visible: isArchived,
                            disabled: restoringId === a.id,
                            testId: `smartimprove-action-restore-${a.id}`,
                            onClick: () => handleRestore(a),
                          },
                          {
                            id: "archive",
                            label: "Archive",
                            icon: ArchiveIcon,
                            // Finalized engagements stay read-only; archive
                            // hidden as before.
                            visible: !isArchived && !a.is_finalized,
                            testId: `smartimprove-action-archive-${a.id}`,
                            onClick: () =>
                              setPendingArchive({
                                id: a.id,
                                company_name: a.company_name,
                              }),
                          },
                          {
                            id: "delete",
                            label: "Delete",
                            icon: Trash2,
                            destructive: true,
                            separatorBefore: true,
                            visible: isAdmin,
                            disabled: a.is_finalized,
                            testId: `smartimprove-action-delete-${a.id}`,
                            onClick: () =>
                              setConfirmDelete({
                                id: a.id,
                                company_name: a.company_name,
                                is_finalized:
                                  a.is_finalized ||
                                  a._status === "Finalized",
                              }),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 ? (
        <p className="text-[10px] text-[#9CA3AF] mt-2">
          Tip: open an engagement to duplicate or finalize it. Finalized
          engagements remain in the list as an immutable record.
        </p>
      ) : null}

      {/* Admin-only delete confirmation */}
      <Dialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && !deleting && setConfirmDelete(null)}
      >
        <DialogContent
          data-testid="smartimprove-delete-modal"
          className="max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-[#111827]">
              Delete engagement?
            </DialogTitle>
            <DialogDescription className="text-sm text-[#6B7280] leading-relaxed">
              Are you sure you want to delete this engagement? This action
              cannot be undone.
              {confirmDelete?.company_name && (
                <>
                  <br />
                  <span className="block mt-2 text-[#111827] font-medium">
                    {confirmDelete.company_name}
                  </span>
                </>
              )}
              {confirmDelete?.is_finalized && (
                <span className="block mt-3 text-[11px] text-[#B91C1C]">
                  This engagement is finalized. The backend will reject the
                  delete to preserve the immutable snapshot.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmDelete(null)}
              disabled={deleting}
              data-testid="smartimprove-delete-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmDelete}
              disabled={deleting || confirmDelete?.is_finalized}
              data-testid="smartimprove-delete-confirm"
              className="bg-[#B91C1C] hover:bg-[#991B1B] text-white"
            >
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ArchiveDialog
        open={!!pendingArchive}
        entityLabel="engagement"
        testIdPrefix="smartimprove-archive"
        onClose={() => setPendingArchive(null)}
        onConfirm={handleConfirmArchive}
      />
    </div>
  );
}
